import { HTMLProps, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { sortByCost } from '../../../features/table/helpers';

export interface SliderProps extends HTMLProps<HTMLDivElement> {
  intervalMs?: number;
  children: ReactNode[];
  perSlide?: number;
  initSlide?: number;
  responsive?: {
    [width: number]: {
      perSlide: number;
      intervalMs?: number;
    };
  };
  desktopSwipe?: boolean;
  count?: number;
}

export const useSlider = (props: SliderProps) => {
  const {
    children,
    intervalMs,
    className,
    perSlide = 1,
    initSlide = 0,
    responsive,
    desktopSwipe = true,
    count,
    ...other
  } = props;

  const slideGap = 16;

  const { elements, slideMaxWidth } = useMemo(() => {
    let slidesPerGroup = perSlide;

    if (responsive) {
      Object.keys(responsive)
        .sort((a, b) => sortByCost(Number(a), Number(b), 'asc'))
        .forEach((width) => {
          const isMatch = window.matchMedia(`(max-width: ${width}px)`).matches;
          if (isMatch) {
            slidesPerGroup = responsive[Number(width)].perSlide;
          }
        });
    }

    const grouped: ReactNode[][] = [];
    for (let i = 0; i < children.length; i += slidesPerGroup) {
      grouped.push(children.slice(i, i + slidesPerGroup));
    }

    const groupedSlides = grouped[1]?.length || 0;
    const maxSlides = groupedSlides < slidesPerGroup ? slidesPerGroup : groupedSlides;
    const slideMaxWidth = `calc(100%/${maxSlides})`;

    return { elements: grouped, slideMaxWidth };
  }, [perSlide, responsive, children]);

  const [currentIndex, setCurrentIndex] = useState(initSlide);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef(0);
  const dragEndX = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    if (typeof count === 'number') {
      setCurrentIndex(0);
    }
  }, [count]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % elements.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + elements.length) % elements.length);
  };

  useEffect(() => {
    if (typeof intervalMs !== 'number') return;

    const interval = setInterval(handleNext, intervalMs);
    return () => clearInterval(interval);
  }, [handleNext, intervalMs, elements.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    setDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging.current) {
      dragEndX.current = e.touches[0].clientX;
      setDragOffset(dragEndX.current - dragStartX.current);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging.current) {
      if (dragOffset < -50) handleNext();
      if (dragOffset > 50) handlePrev();
      isDragging.current = false;
      dragStartX.current = 0;
      dragEndX.current = 0;
      setDragging(false);
      setDragOffset(0);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!desktopSwipe) return;
    dragStartX.current = e.clientX;
    isDragging.current = true;
    setDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!desktopSwipe || !isDragging.current) return;
    dragEndX.current = e.clientX;
    setDragOffset(dragEndX.current - dragStartX.current);
  };

  const handleDragEnd = () => {
    if (!desktopSwipe || !isDragging.current) return;

    if (dragOffset > 50) handlePrev();
    if (dragOffset < -50) handleNext();

    isDragging.current = false;
    dragStartX.current = 0;
    dragEndX.current = 0;
    setDragging(false);
    setDragOffset(0);
  };

  const isExistNext = currentIndex < elements.length - 1;
  const isExistPrev = currentIndex > 0;

  return {
    currentIndex,
    elements,
    slideMaxWidth,
    dragging,
    dragOffset,
    isExistNext,
    isExistPrev,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleDragEnd,
    handleNext,
    handlePrev,
    className,
    other,
    slideGap,
  };
};
